import { Box, HStack, Table, TableContainer, Th, Thead, Tr } from '@chakra-ui/react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingFn,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React from 'react';

import PreviewListSkeleton from '@/components/preview-list-skeleton/PreviewListSkeleton';
import SideControls from '@/components/side-controls/SideControls';
import VirtualTableBody from '@/components/virtual-table/virtual-table-body/VirtualTableBody';
import {
  DEFAULT_ARRAY_START_INDEX,
  DEFAULT_MAX_WIDTH_TABLE_SIZE,
  DEFAULT_TABLE_HEIGHT_OFFSET,
  DEFAULT_TABLE_SIZE,
  DEFAULT_TABLE_WIDTH,
  EMPTY_ARRAY_LENGTH,
  NUMERICAL_ONE,
  NUMERICAL_ZERO,
} from '@/constants/defaults';
import { DEFAULT_DRAWER_TOP_POSITION } from '@/constants/ui';
import { CellColor, HeaderSortHandler, HeaderSortHandlers } from '@/types/ui.types';
import { setsAreEqual } from '@/utils/arrays';
import SortingUtils from '@/utils/sorting';

import styles from './styles.module.scss';

// Offset to include/exclude necessary items when using array.slice
const SLICE_OFFSET = 2;

export interface VirtualTableProps {
  // React-Table has typing issues, and I am not screwing around with it
  // disregard the terrible use of any here
  // https://github.com/TanStack/table/issues/4241
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  handleUpdateOrder: (updatedData: unknown[]) => void;
  uniqueRowKey: string;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: VisibilityState;
  dataTestId?: string;
  handleColorPicker?: () => void;
  handleQuickColorPicker?: (color: CellColor) => void;
  headerSortHandlers?: HeaderSortHandlers;
  hideSortingTable?: boolean;
  isLoading?: boolean;
  maxTableWidth?: string;
  noDataFoundMessage?: string;
  overrideHeader?: boolean;
  overrideTableSorting?: boolean;
  showColorPicker?: boolean;
  showSideControls?: boolean;
  sortingFns?: Record<string, SortingFn<unknown>>;
  sortingState?: SortingState;
  tableRowHeight?: number;
  tableWidth?: string;
  tableInstanceRef?: React.MutableRefObject<unknown>;
  topPosition?: string;
  uniqueSelectedIds?: Set<number | string>;
  TableHeader?: React.ReactNode;
  TableHeaderButton?: React.ReactNode;
}

const VirtualTable = (props: VirtualTableProps): React.JSX.Element => {
  const {
    columnFilters = undefined,
    columns,
    columnVisibility = undefined,
    data,
    dataTestId,
    handleColorPicker = () => void {},
    handleQuickColorPicker = () => void {},
    handleUpdateOrder,
    headerSortHandlers,
    hideSortingTable,
    isLoading = false,
    maxTableWidth,
    noDataFoundMessage = 'No data found',
    overrideHeader = false,
    overrideTableSorting = false,
    showColorPicker = true,
    showSideControls = false,
    sortingFns = {},
    sortingState = [],
    TableHeader = null,
    TableHeaderButton = null,
    tableInstanceRef = undefined,
    tableRowHeight,
    tableWidth,
    topPosition,
    uniqueRowKey,
    uniqueSelectedIds = new Set<number>(),
  } = props;

  const tableInstance = useReactTable({
    autoResetAll: false,
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    getSortedRowModel: getSortedRowModel(),
    manualSorting: overrideTableSorting,
    sortingFns,
    state: {
      columnFilters,
      columnVisibility,
      sorting: sortingState,
    },
  });

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (tableInstanceRef) {
      tableInstanceRef.current = tableInstance;
    }
  });

  // --- Side Buttons handlers Start
  const handleMoveItemsTop = React.useCallback(() => {
    // Reset table sorting so the custom ordering does not get overwritten by the asc/desc table sorting functionality
    const items = [...data];
    const selectedItems = items.filter((item) => uniqueSelectedIds.has(item[uniqueRowKey]));

    if (items.length > EMPTY_ARRAY_LENGTH && selectedItems.length > EMPTY_ARRAY_LENGTH) {
      // Ids of the items that are on the top of the list
      const topItemsIds = new Set(
        items.slice(DEFAULT_ARRAY_START_INDEX, selectedItems.length).map((item) => item[uniqueRowKey]),
      );

      // Items are already on top if the items Ids that are on top of the list are equal with the selected items Ids
      const itemsAlreadyOnTop = setsAreEqual(topItemsIds, uniqueSelectedIds);
      // Do not re-order in case the items are already on top
      if (itemsAlreadyOnTop) {
        return;
      }

      // Arrange the selected items as the first items on the array and then the remaining items
      const newData = [...selectedItems, ...items.filter((item) => !uniqueSelectedIds.has(item[uniqueRowKey]))];

      handleUpdateOrder(newData);
    }
  }, [data, uniqueSelectedIds, uniqueRowKey, handleUpdateOrder]);

  // Check if the indices of the selected items are consecutive
  const areConsecutiveIndices = React.useCallback(
    (selectedIndices: number[]) =>
      [...selectedIndices]
        .sort((a, b) => a - b)
        .every(
          (value, index, array) => index === NUMERICAL_ZERO || value === array[index - NUMERICAL_ONE] + NUMERICAL_ONE,
        ),
    [],
  );

  const itemIndexMap = React.useMemo(() => {
    const map = new Map();
    data.forEach((item, index) => {
      map.set(item[uniqueRowKey], index);
    });
    return map;
  }, [data, uniqueRowKey]);

  const handleMoveItemsUp = React.useCallback(() => {
    // Reset table sorting so the custom ordering does not get overwritten by the asc/desc table sorting functionality
    const items = [...data];
    const selectedItems = items.filter((item) => uniqueSelectedIds.has(item[uniqueRowKey]));
    const selectedIndices = selectedItems.map((selectedItem) => itemIndexMap.get(selectedItem[uniqueRowKey]));

    if (items.length > EMPTY_ARRAY_LENGTH && selectedItems.length > EMPTY_ARRAY_LENGTH) {
      // The position of the first selected item on the list
      const positionToSlice = items.findIndex(
        (item) => item[uniqueRowKey] === selectedItems[DEFAULT_ARRAY_START_INDEX][uniqueRowKey],
      );

      // Move items to the top if the selection includes the first item on the list
      if (positionToSlice < NUMERICAL_ONE) {
        handleMoveItemsTop();
        return;
      }
      const sliceEndIndex = areConsecutiveIndices(selectedIndices) ? positionToSlice - NUMERICAL_ONE : positionToSlice;
      // Items that will not move
      const firstPart = items.slice(DEFAULT_ARRAY_START_INDEX, sliceEndIndex);

      // Items that will be shifted down (remove the selected items)
      const lastPart = items.slice(sliceEndIndex).filter((item) => !uniqueSelectedIds.has(item[uniqueRowKey]));

      // Arrange the items
      const newData = [...firstPart, ...selectedItems, ...lastPart];

      handleUpdateOrder(newData);
    }
  }, [
    areConsecutiveIndices,
    data,
    handleMoveItemsTop,
    handleUpdateOrder,
    itemIndexMap,
    uniqueRowKey,
    uniqueSelectedIds,
  ]);

  const handleMoveItemsBottom = React.useCallback(() => {
    // Reset table sorting so the custom ordering does not get overwritten by the asc/desc table sorting functionality
    const items = [...data];
    const selectedItems = items.filter((item) => uniqueSelectedIds.has(item[uniqueRowKey]));

    if (items.length > EMPTY_ARRAY_LENGTH && selectedItems.length > EMPTY_ARRAY_LENGTH) {
      // Ids of the items that are on the bottom of the list
      const bottomItemsIds = new Set(
        items
          .slice(Math.max(items.length - selectedItems.length, DEFAULT_ARRAY_START_INDEX))
          .map((item) => item[uniqueRowKey]),
      );

      // Items are already on the bottom if the items Ids that are on the bottom of the list are equal with the selected items Ids
      const itemsAlreadyOnBottom = setsAreEqual(bottomItemsIds, uniqueSelectedIds);
      // Do not re-order in case the items are already on the bottom
      if (itemsAlreadyOnBottom) {
        return;
      }

      // Arrange the remaining items as the last items on the array and then the selected items
      const newData = [...items.filter((item) => !uniqueSelectedIds.has(item[uniqueRowKey])), ...selectedItems];

      handleUpdateOrder(newData);
    }
  }, [data, uniqueSelectedIds, uniqueRowKey, handleUpdateOrder]);

  const handleMoveItemsDown = React.useCallback(() => {
    // Reset table sorting so the custom ordering does not get overwritten by the asc/desc table sorting functionality
    const items = [...data];

    const selectedItems = items.filter((item) => uniqueSelectedIds.has(item[uniqueRowKey]));

    if (items.length > EMPTY_ARRAY_LENGTH && selectedItems.length > EMPTY_ARRAY_LENGTH) {
      // The position of the last selected item on the list
      const positionToSlice = items.findIndex(
        (item) => item[uniqueRowKey] === selectedItems[selectedItems.length - NUMERICAL_ONE][uniqueRowKey],
      );

      // Move items to the bottom if the selection includes any of last two items on the list

      if (positionToSlice > items.length - SLICE_OFFSET) {
        handleMoveItemsBottom();
        return;
      }

      const selectedIndices = selectedItems.map((selectedItem) => itemIndexMap.get(selectedItem[uniqueRowKey]));
      const offset = areConsecutiveIndices(selectedIndices) ? SLICE_OFFSET : NUMERICAL_ONE;
      const sliceEndIndex = positionToSlice + offset;

      // Items that will be shifted up (remove the selected items)
      // Add 2 to include the last element (as slice is exclusive to the end parameter)
      const firstPart = items
        .slice(DEFAULT_ARRAY_START_INDEX, sliceEndIndex)
        .filter((item) => !uniqueSelectedIds.has(item[uniqueRowKey]));

      // Items that will be shifted down
      // Add offset to exclude the first element (it is included on the firstPart to be moved up)

      const lastPart = items.slice(sliceEndIndex);

      // Arrange the items
      const newData = [...firstPart, ...selectedItems, ...lastPart];

      handleUpdateOrder(newData);
    }
  }, [
    areConsecutiveIndices,
    data,
    handleMoveItemsBottom,
    handleUpdateOrder,
    itemIndexMap,
    uniqueRowKey,
    uniqueSelectedIds,
  ]);

  // --- Side Buttons handlers End

  // Color picker is disabled if there are no selected items
  const noSelectedItems = uniqueSelectedIds.size === EMPTY_ARRAY_LENGTH;

  return (
    <HStack alignItems="flex-start" gap={0} width="100%">
      <Box borderRadius="lg" borderWidth="1px" maxWidth={maxTableWidth} p={2}>
        <Box className={styles.container} top={topPosition ?? DEFAULT_DRAWER_TOP_POSITION}>
          {TableHeader}
          <Table maxWidth={maxTableWidth}>
            <Thead
              borderBottom={'2px solid #DADDDE'}
              cursor={hideSortingTable ? 'not-allowed' : 'pointer'}
              display={'table-caption'}
              position={'sticky'}
              top={'0'}
            >
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <Tr alignItems={'center'} display={'flex'} key={headerGroup.id} w={'100%'}>
                  {headerGroup.headers.map((header) => {
                    const headerSortDirection = tableInstance.getState().sorting.find((s) => s.id === header.id)?.desc;
                    const headerSortHandler = headerSortHandlers?.find(
                      (h: HeaderSortHandler) => h.columnId === header.column.id,
                    )?.sortHandler;

                    const canSort = header.column.getCanSort();
                    const isSorted = header.column.getIsSorted();
                    const nextSorting = header.column.getNextSortingOrder();

                    return overrideHeader ? (
                      <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                    ) : (
                      <Th key={header.id}>
                        <HStack
                          color={canSort ? 'gray.600' : '#afafaf'}
                          h={'32px'}
                          maxW={maxTableWidth ?? `${DEFAULT_TABLE_SIZE}px`}
                          onClick={headerSortHandler}
                          style={{ pointerEvents: canSort ? 'unset' : 'none' }}
                          title={SortingUtils.getSortingTitle(canSort, nextSorting)}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {SortingUtils.getSortingIcon(isSorted, headerSortDirection)}
                        </HStack>
                      </Th>
                    );
                  })}
                  {TableHeaderButton}
                </Tr>
              ))}
            </Thead>
          </Table>
        </Box>
        <TableContainer
          className={styles.table}
          // Override Chakra focus-visible outline
          _focusVisible={{border: 'none', outline: 'none'}}
          maxH={`${DEFAULT_TABLE_SIZE}px`}
          maxWidth={maxTableWidth ?? `${DEFAULT_MAX_WIDTH_TABLE_SIZE}px`}
          minH={`${DEFAULT_TABLE_SIZE + DEFAULT_TABLE_HEIGHT_OFFSET}px`}
          minW={tableWidth ?? `${DEFAULT_TABLE_WIDTH}px`}
          overflowX={'hidden'}
          overflowY={'auto'}
          overscrollBehaviorY={'auto'}
          ref={containerRef}
          tabIndex={0}
          w={'100%'}
        >
          <Table maxWidth={maxTableWidth} overflowY={'clip'}>
            {isLoading ? (
              <PreviewListSkeleton />
            ) : (
              <VirtualTableBody
                dataTestId={dataTestId}
                noDataFoundMessage={noDataFoundMessage}
                maxTableWidth={maxTableWidth}
                table={tableInstance}
                tableContainerRef={containerRef}
                tableRowHeight={tableRowHeight}
                tableWidth={tableWidth}
                uniqueRowKey={uniqueRowKey}
                uniqueSelectedIds={uniqueSelectedIds}
              />
            )}
          </Table>
        </TableContainer>
      </Box>
      {showSideControls && (
        <SideControls
          colorPickerDisabled={noSelectedItems}
          onColorPickerClick={handleColorPicker}
          onMoveBottomClick={handleMoveItemsBottom}
          onMoveDownClick={handleMoveItemsDown}
          onMoveTopClick={handleMoveItemsTop}
          onMoveUpClick={handleMoveItemsUp}
          onQuickColorPickerClick={handleQuickColorPicker}
          showColorPicker={showColorPicker}
          sortingDisabled={noSelectedItems || !overrideTableSorting}
        />
      )}
    </HStack>
  );
};

export default VirtualTable;
